import React, { useState, useEffect } from "react";
import {
  Form,
  Input,
  Button,
  Select,
  Space,
  Divider,
  Card,
  Typography,
  message,
  InputNumber,
  Row,
  Col,
  Tooltip,
  Modal,
  Tag,
  Table,
} from "antd";
import {
  PlusOutlined,
  DeleteOutlined,
  LoadingOutlined,
  InfoCircleOutlined,
} from "@ant-design/icons";
import { Invoice, PaymentTerm } from "../../types/invoice";
import { supabase } from "../../lib/supabase";
import { Booking, Product } from "../../types/booking";
import { createInvoice, updateInvoice } from "../../services/invoiceService";
import { SupabaseClient } from "@supabase/supabase-js";
import InvoicePreview from "./InvoicePreview";
import { generateInvoiceLinesFromPricing } from "../../services/pricingCalculationService";
import { AIR_FREIGHT } from "../../lib/pricingDefaults";

import dayjs from "dayjs";
import {
  BookingSelector,
  CustomsSection,
  PaymentTermsSection,
  LMDSection,
} from "./form-sections";

const { Text } = Typography;
const { Option } = Select;

// Type cast supabase to avoid TypeScript errors
const typedSupabase = supabase as unknown as SupabaseClient;

// Helper function to calculate time difference in seconds
const getSecondsAgo = (date: Date): number => {
  return Math.floor((new Date().getTime() - date.getTime()) / 1000);
};

// Define types for pricing components
interface PricingComponent {
  id: number;
  name: string;
  component_type: string;
  description?: string;
  calculation_type?: string;
}

// Define types for container sizes
interface ContainerSize {
  id: number;
  name: string;
  created_at?: string;
}

// Define types for form values
interface FormValues {
  booking_id?: number;
  currency: string;
  payment_term: PaymentTerm;
  custom_payment_terms: string;
  lines: Array<{
    id?: number;
    description: string;
    quantity: number;
    unit_price: number;
    vat_percentage: number;
    cost_type?: string;
  }>;
}

interface InvoiceFormProps {
  onSuccess: (invoice: Invoice) => void;
  onCancel: () => void;
  initialBookingId?: number;
  editInvoice?: Invoice; // Add prop for editing existing invoice
}

const InvoiceForm: React.FC<InvoiceFormProps> = ({
  onSuccess,
  onCancel,
  initialBookingId,
  editInvoice,
}) => {
  const [form] = Form.useForm();
  const [loading, setLoading] = useState(false);
  const [bookings, setBookings] = useState<Booking[]>([]);
  const [pricingComponents, setPricingComponents] = useState<
    PricingComponent[]
  >([]);
  const [selectedBooking, setSelectedBooking] = useState<Booking | null>(null);
  const [formValues, setFormValues] = useState<FormValues>({
    currency: "EUR",
    payment_term: PaymentTerm.DAYS_30,
    custom_payment_terms: "",
    lines: [
      {
        description: "",
        quantity: 1,
        unit_price: 0,
        vat_percentage: 0,
      },
    ],
  });
  const [autogeneratingLines, setAutogeneratingLines] = useState(false);
  const [pricingInfoVisible, setPricingInfoVisible] = useState(false);

  // Add state for container sizes
  const [containerSizes, setContainerSizes] = useState<ContainerSize[]>([]);

  // Add state for tariff calculation type
  const [tariffCalculationType, setTariffCalculationType] = useState<
    "per_kg" | "per_cbm" | "per_container"
  >("per_kg");

  // Add state for tariff row data
  const [tariffRow, setTariffRow] = useState<{
    unit_price: number;
    vat_percentage: number;
    container_type_id?: number;
    manual_quantity?: number;
    use_manual_quantity: boolean;
  }>({
    unit_price: 0,
    vat_percentage: 0,
    use_manual_quantity: false,
  });

  // Add state for tariff suggestions
  const [suggestedTariffs, setSuggestedTariffs] = useState<
    Array<{
      id: number;
      price: number;
      description: string;
      from: string | null;
      to: string | null;
      isSmartMatch?: boolean;
      matchReasons?: string[];
      bracketDisplay?: string | null;
      bookingType?: string | null;
      containerSize?: string | null;
    }>
  >([]);
  const [loadingSuggestions, setLoadingSuggestions] = useState(false);
  const [selectedTariffId, setSelectedTariffId] = useState<number | null>(null);

  // Add auto-save state
  const [autoSaving, setAutoSaving] = useState(false);
  const [lastSaved, setLastSaved] = useState<Date | null>(null);
  const [savingLineIndex, setSavingLineIndex] = useState<number | null>(null);

  // Add state for preview modal
  const [previewModalVisible, setPreviewModalVisible] = useState(false);

  // Determine if we're in edit mode
  const isEditMode = !!editInvoice;

  // Helper function to check if a tariff's container size matches the selected container
  const isMatchingContainer = (tariffContainerSize: string | null) => {
    return (
      tariffCalculationType === "per_container" &&
      tariffRow.container_type_id &&
      containerSizes.find((c) => c.id === tariffRow.container_type_id)?.name ===
        tariffContainerSize
    );
  };

  // Fetch bookings and pricing components when component mounts
  useEffect(() => {
    fetchBookings();
    fetchPricingComponents();
    fetchContainerSizes();
  }, []);

  // Set initial data for edit mode
  useEffect(() => {
    if (editInvoice && bookings.length > 0) {
      // Find the booking for this invoice
      const booking = bookings.find((b) => b.id === editInvoice.booking_id);
      if (booking) {
        setSelectedBooking(booking);
      }

      // Set form values from existing invoice
      const editFormValues: FormValues = {
        booking_id: editInvoice.booking_id,
        currency: editInvoice.currency,
        payment_term: editInvoice.payment_term || PaymentTerm.DAYS_30,
        custom_payment_terms: editInvoice.custom_payment_terms || "",
        lines: editInvoice.lines.map((line) => ({
          id: line.id,
          description: line.description,
          quantity: line.quantity,
          unit_price: line.unit_price,
          vat_percentage: 0, // Always set VAT to 0
          cost_type: line.cost_type,
        })),
      };

      // Set form values
      form.setFieldsValue(editFormValues);
      setFormValues(editFormValues);
    }
  }, [editInvoice, bookings, form]);

  // Set initial booking if provided (for create mode)
  useEffect(() => {
    if (!isEditMode && initialBookingId && bookings.length > 0) {
      const booking = bookings.find((b) => b.id === initialBookingId);
      if (booking) {
        form.setFieldsValue({ booking_id: initialBookingId });
        setSelectedBooking(booking);

        // Get company payment term if available
        if (booking.company_info) {
          const companyInfo = Array.isArray(booking.company_info)
            ? booking.company_info[0]
            : booking.company_info;

          if (companyInfo?.pricing_payment_term) {
            // The pricing_payment_term is already a full object from the join
            const paymentTermData = Array.isArray(
              companyInfo.pricing_payment_term
            )
              ? companyInfo.pricing_payment_term[0]
              : companyInfo.pricing_payment_term;

            if (paymentTermData && paymentTermData.days !== undefined) {
              // Set the appropriate payment term enum value based on days
              let paymentTermValue = PaymentTerm.DAYS_30; // Default
              let customPaymentTerms = "";

              if (paymentTermData.days === 7) {
                paymentTermValue = PaymentTerm.DAYS_7;
              } else if (paymentTermData.days === 14) {
                paymentTermValue = PaymentTerm.DAYS_14;
              } else if (paymentTermData.days === 30) {
                paymentTermValue = PaymentTerm.DAYS_30;
              } else if (paymentTermData.days === 60) {
                paymentTermValue = PaymentTerm.DAYS_60;
              } else {
                // For non-standard day values, use custom payment terms
                paymentTermValue = PaymentTerm.CUSTOM;
                customPaymentTerms = `Payment due within ${paymentTermData.days} days`;
              }

              form.setFieldsValue({
                payment_term: paymentTermValue,
                custom_payment_terms: customPaymentTerms,
              });
              setFormValues((prev) => ({
                ...prev,
                payment_term: paymentTermValue,
                custom_payment_terms: customPaymentTerms,
              }));
            } else {
              // No payment term data found - set default
              const defaultPaymentTerm = PaymentTerm.DAYS_30;
              form.setFieldsValue({ payment_term: defaultPaymentTerm });
              setFormValues((prev) => ({
                ...prev,
                payment_term: defaultPaymentTerm,
              }));
            }
          } else {
            // No payment term found for this company - set default
            const defaultPaymentTerm = PaymentTerm.DAYS_30;
            form.setFieldsValue({ payment_term: defaultPaymentTerm });
            setFormValues((prev) => ({
              ...prev,
              payment_term: defaultPaymentTerm,
            }));
          }
        }

        // Only auto-generate lines in create mode
        autogenerateInvoiceLines(booking);
      }
    }
  }, [initialBookingId, bookings, form, isEditMode]);

  // Update tariff calculation type when booking changes
  useEffect(() => {
    if (selectedBooking && selectedBooking.transport_type_info) {
      const transportTypeId = Array.isArray(selectedBooking.transport_type_info)
        ? selectedBooking.transport_type_info[0]?.id
        : selectedBooking.transport_type_info.id;

      if (transportTypeId === 1) {
        // Air freight
        setTariffCalculationType("per_kg");
      } else if (transportTypeId === 2) {
        // Sea freight - check booking type
        const bookingType = selectedBooking.booking_type_info
          ? Array.isArray(selectedBooking.booking_type_info)
            ? selectedBooking.booking_type_info[0]?.name
            : selectedBooking.booking_type_info.name
          : null;

        if (bookingType === "FCL") {
          setTariffCalculationType("per_container");
        } else if (bookingType === "LCL") {
          setTariffCalculationType("per_cbm");
        } else {
          // Default to per_cbm for sea freight when booking type is unknown
          setTariffCalculationType("per_cbm");
        }
      } else {
        // Other transport types - default to per_cbm
        setTariffCalculationType("per_cbm");
      }

      // Fetch tariff suggestions when booking changes
      fetchTariffSuggestions();
    }
  }, [selectedBooking]);

  // Fetch tariff suggestions when calculation type changes
  useEffect(() => {
    if (selectedBooking && tariffCalculationType) {
      fetchTariffSuggestions();
    }
  }, [tariffCalculationType]);

  // Add debounced auto-save effect
  useEffect(() => {
    if (!isEditMode || !editInvoice || !selectedBooking) {
      return;
    }

    // Debounce auto-save to avoid too many calls
    const timeoutId = setTimeout(() => {
      autoSaveInvoice(formValues);
    }, 1000); // Auto-save after 1 second of no changes

    return () => clearTimeout(timeoutId);
  }, [formValues, isEditMode, editInvoice, selectedBooking]);

  // Fetch bookings from Supabase
  const fetchBookings = async () => {
    try {
      const { data, error } = await typedSupabase
        .from("bookings")
        .select(
          `
          *,
          transport_type_info:booking_transport_types(*),
          booking_type_info:bookings_type(*),
          company_info:company(
            *,
            pricing_payment_term:pricing_payment_terms(*)
          ),
          origin_port_info:booking_origin_port(*),
          destination_port_info:booking_destination_port(*),
          booking_incoterm_info:booking_incoterm(*),
          container_info:containers(
            id,
            reference,
            etd,
            eta,
            origin_port:booking_origin_port(*),
            destination_port:booking_destination_port(*)
          ),
          product_info:booking_product(*)
        `
        )
        .order("created_at", { ascending: false });

      if (error) {
        throw error;
      }

      if (data) {
        setBookings(data);
      }
    } catch (error) {
      console.error("Error fetching bookings:", error);
      message.error("Failed to load bookings");
    }
  };

  // Fetch pricing components from Supabase
  const fetchPricingComponents = async () => {
    try {
      const { data, error } = await typedSupabase
        .from("pricing_components")
        .select("*")
        .order("name");

      if (error) {
        throw error;
      }

      if (data) {
        setPricingComponents(data);
      }
    } catch (error) {
      console.error("Error fetching pricing components:", error);
      message.error("Failed to load pricing components");
    }
  };

  // Auto-generate invoice lines from pricing components based on booking
  const autogenerateInvoiceLines = async (booking: Booking) => {
    try {
      setAutogeneratingLines(true);

      // Determine tariff date information
      let tariffDateInfo = "using current date";
      if (booking.container_info) {
        const containerInfo = Array.isArray(booking.container_info)
          ? booking.container_info[0]
          : booking.container_info;

        if (containerInfo && containerInfo.etd) {
          const etdDate = new Date(containerInfo.etd);
          tariffDateInfo = `using container ETD date (${etdDate.toLocaleDateString()})`;
        }
      }

      const { lines, currency, error /* ruleSelectionInfo */ } =
        await generateInvoiceLinesFromPricing(
          booking,
          booking.company_id || undefined // Pass company ID for pricing overrides, handle null case
        );

      if (error) {
        throw error;
      }

      if (lines.length > 0) {
        // Check if a tariff line is included
        const hasTariffLine = lines.some((line) => line.cost_type === "tariff");

        // Ensure all VAT values are set to 0
        const linesWithZeroVAT = lines.map((line) => ({
          ...line,
          vat_percentage: 0,
        }));

        // First, update the form with the generated lines
        form.setFieldsValue({
          lines: linesWithZeroVAT,
          currency,
        });

        // Update form values for preview
        setFormValues((prev: FormValues) => ({
          ...prev,
          lines: linesWithZeroVAT,
          currency,
        }));

        message.success(
          `Generated ${lines.length} invoice line(s) from pricing rules ${tariffDateInfo}`
        );

        if (!hasTariffLine) {
          message.warning(
            "No tariff was found for this booking. You can add a manual tariff line item below."
          );
        }
      } else {
        message.info(
          `No pricing rules or tariffs matched this booking ${tariffDateInfo}. Add lines manually.`
        );
      }
    } catch (error) {
      console.error("Error generating invoice lines:", error);
      message.error("Failed to generate invoice lines from pricing");
    } finally {
      setAutogeneratingLines(false);
    }
  };

  // Generate invoice number when component mounts
  useEffect(() => {
    // No longer generating invoice numbers automatically
  }, [form, isEditMode]);

  // Handle booking selection change
  const handleBookingChange = async (value: number) => {
    const booking = bookings.find((b) => b.id === value);
    setSelectedBooking(booking || null);

    // Update form values for preview
    setFormValues((prev: FormValues) => ({ ...prev, booking_id: value }));

    // Get company payment term if available
    if (booking?.company_info) {
      const companyInfo = Array.isArray(booking.company_info)
        ? booking.company_info[0]
        : booking.company_info;

      if (companyInfo?.pricing_payment_term) {
        // The pricing_payment_term is already a full object from the join
        const paymentTermData = Array.isArray(companyInfo.pricing_payment_term)
          ? companyInfo.pricing_payment_term[0]
          : companyInfo.pricing_payment_term;

        if (paymentTermData && paymentTermData.days !== undefined) {
          // Set the appropriate payment term enum value based on days
          let paymentTermValue = PaymentTerm.DAYS_30; // Default
          let customPaymentTerms = "";

          if (paymentTermData.days === 7) {
            paymentTermValue = PaymentTerm.DAYS_7;
          } else if (paymentTermData.days === 14) {
            paymentTermValue = PaymentTerm.DAYS_14;
          } else if (paymentTermData.days === 30) {
            paymentTermValue = PaymentTerm.DAYS_30;
          } else if (paymentTermData.days === 60) {
            paymentTermValue = PaymentTerm.DAYS_60;
          } else {
            // For non-standard day values, use custom payment terms
            paymentTermValue = PaymentTerm.CUSTOM;
            customPaymentTerms = `Payment due within ${paymentTermData.days} days`;
          }

          form.setFieldsValue({
            payment_term: paymentTermValue,
            custom_payment_terms: customPaymentTerms,
          });
          setFormValues((prev) => ({
            ...prev,
            payment_term: paymentTermValue,
            custom_payment_terms: customPaymentTerms,
          }));
        } else {
          // No payment term data found - set default
          const defaultPaymentTerm = PaymentTerm.DAYS_30;
          form.setFieldsValue({ payment_term: defaultPaymentTerm });
          setFormValues((prev) => ({
            ...prev,
            payment_term: defaultPaymentTerm,
          }));
        }
      } else {
        // No payment term found for this company - set default
        const defaultPaymentTerm = PaymentTerm.DAYS_30;
        form.setFieldsValue({ payment_term: defaultPaymentTerm });
        setFormValues((prev) => ({
          ...prev,
          payment_term: defaultPaymentTerm,
        }));
      }
    }

    // Only auto-generate invoice lines for create mode
    if (!isEditMode && booking) {
      autogenerateInvoiceLines(booking);
    }
  };

  // Handle form values change for live preview
  const handleValuesChange = (
    changedValues: unknown,
    allValues: FormValues
  ) => {
    // Update all form values for preview
    setFormValues(allValues);
  };

  // Handle form submission
  const handleSubmit = async (values: FormValues) => {
    try {
      setLoading(true);

      if (isEditMode && editInvoice) {
        // Update existing invoice
        const invoiceData: Partial<Invoice> = {
          booking_id: values.booking_id!,
          company_id: selectedBooking?.company_info
            ? Array.isArray(selectedBooking.company_info)
              ? selectedBooking.company_info[0]?.id
              : selectedBooking.company_info.id
            : undefined,
          currency: values.currency,
          payment_term: values.payment_term,
          custom_payment_terms: values.custom_payment_terms,
          lines: values.lines.map((line) => ({
            // Use the line ID directly from the form values
            id: line.id,
            description: line.description,
            quantity: line.quantity || 0,
            unit_price: line.unit_price || 0,
            vat_percentage: line.vat_percentage || 0,
            cost_type: line.cost_type,
          })),
        };

        const { data, error } = await updateInvoice(
          editInvoice.id!,
          invoiceData
        );

        if (error) {
          throw error;
        }

        message.success("Invoice updated successfully");

        if (data) {
          onSuccess(data);
        }
      } else {
        // Create new invoice
        const invoiceData: Invoice = {
          booking_id: values.booking_id!,
          company_id: selectedBooking?.company_info
            ? Array.isArray(selectedBooking.company_info)
              ? selectedBooking.company_info[0]?.id
              : selectedBooking.company_info.id
            : undefined,
          currency: "EUR", // Default to EUR
          payment_term: values.payment_term,
          custom_payment_terms: values.custom_payment_terms,
          lines: values.lines.map((line) => ({
            id: line.id,
            description: line.description,
            quantity: line.quantity || 0,
            unit_price: line.unit_price || 0,
            vat_percentage: line.vat_percentage || 0,
            cost_type: line.cost_type,
          })),
        };

        const { data, error } = await createInvoice(invoiceData);

        if (error) {
          throw error;
        }

        message.success("Invoice created successfully");

        if (data) {
          onSuccess(data);
        }
      }
    } catch (error) {
      console.error(
        `Error ${isEditMode ? "updating" : "creating"} invoice:`,
        error
      );
      message.error(`Failed to ${isEditMode ? "update" : "create"} invoice`);
    } finally {
      setLoading(false);
    }
  };

  // Helper to generate booking display name
  const getBookingDisplayName = (booking: Booking) => {
    // Always use booking ID instead of reference
    const bookingId = `EFF${booking.id}`;
    const transportType = booking.transport_type_info
      ? Array.isArray(booking.transport_type_info)
        ? booking.transport_type_info[0]?.abbreviation || ""
        : booking.transport_type_info.abbreviation || ""
      : "";

    // Get transport type ID to check if it's seafreight (transport_type 2)
    const transportTypeId = booking.transport_type_info
      ? Array.isArray(booking.transport_type_info)
        ? booking.transport_type_info[0]?.id
        : booking.transport_type_info.id
      : null;

    // Add booking type for seafreight (transport_type 2)
    let bookingTypeInfo = "";
    if (transportTypeId === 2 && booking.booking_type_info) {
      const bookingType = Array.isArray(booking.booking_type_info)
        ? booking.booking_type_info[0]?.name || ""
        : booking.booking_type_info.name || "";

      if (bookingType) {
        bookingTypeInfo = `-${bookingType}`;
      }
    }

    const company = booking.company_info
      ? Array.isArray(booking.company_info)
        ? booking.company_info[0]?.name || "Unknown"
        : booking.company_info.name || "Unknown"
      : "Unknown";

    return `${bookingId}${transportType}${bookingTypeInfo} - ${company}`;
  };

  // Calculate total amount
  const calculateTotal = (lines: FormValues["lines"]) => {
    if (!lines || !Array.isArray(lines)) return 0;

    return lines.reduce((sum, line) => {
      if (!line) return sum;
      const lineTotal = (line.quantity || 0) * (line.unit_price || 0);
      return sum + lineTotal;
    }, 0);
  };

  // Get origin port information, prioritizing container origin if available
  const getOriginPort = (booking: Booking) => {
    // Check if booking has container with origin port
    if (booking.container_info) {
      const containerInfo = Array.isArray(booking.container_info)
        ? booking.container_info[0]
        : booking.container_info;

      if (containerInfo?.origin_port) {
        const originPort = Array.isArray(containerInfo.origin_port)
          ? containerInfo.origin_port[0]
          : containerInfo.origin_port;

        if (originPort && originPort.name) {
          return {
            name: originPort.name,
            isFromContainer: true,
            containerRef: containerInfo.reference,
          };
        }
      }
    }

    // Fallback to booking origin port
    const originPort =
      booking.origin_port_info &&
      (Array.isArray(booking.origin_port_info)
        ? booking.origin_port_info[0]
        : booking.origin_port_info);

    return {
      name: originPort?.name || "Unknown",
      isFromContainer: false,
    };
  };

  // Get destination port information, prioritizing container destination if available
  const getDestinationPort = (booking: Booking) => {
    // Check if booking has container with destination port
    if (booking.container_info) {
      const containerInfo = Array.isArray(booking.container_info)
        ? booking.container_info[0]
        : booking.container_info;

      if (containerInfo?.destination_port) {
        const destPort = Array.isArray(containerInfo.destination_port)
          ? containerInfo.destination_port[0]
          : containerInfo.destination_port;

        if (destPort && destPort.name) {
          return {
            name: destPort.name,
            isFromContainer: true,
            containerRef: containerInfo.reference,
          };
        }
      }
    }

    // Fallback to booking destination port
    const destPort =
      booking.destination_port_info &&
      (Array.isArray(booking.destination_port_info)
        ? booking.destination_port_info[0]
        : booking.destination_port_info);

    return {
      name: destPort?.name || "Unknown",
      isFromContainer: false,
    };
  };

  // Get flattened product list from booking
  const getBookingProducts = (booking: Booking): Product[] => {
    if (!booking.product_info) return [];

    // Handle different structures of product_info from booking_product table
    if (Array.isArray(booking.product_info)) {
      // Already an array of products
      return booking.product_info as unknown as Product[];
    }

    // Single product
    return [booking.product_info as unknown as Product];
  };

  // Get total weight from booking products
  const getTotalWeightFromBooking = (booking: Booking): number => {
    const products = getBookingProducts(booking);
    return products.reduce(
      (sum, product) => sum + (product.gross_weight || 0),
      0
    );
  };

  // Get total volume from booking products
  const getTotalVolumeFromBooking = (booking: Booking): number => {
    const products = getBookingProducts(booking);
    return products.reduce((sum, product) => sum + (product.cbm || 0), 0);
  };

  // Calculate chargeable weight for air freight (maximum of actual weight and volumetric weight)
  const calculateChargeableWeight = (booking: Booking) => {
    const totalWeight = getTotalWeightFromBooking(booking);
    const totalVolume = getTotalVolumeFromBooking(booking);

    // For air freight, calculate volumetric weight (1 CBM = 167 kg)
    const volumetricWeight = totalVolume * AIR_FREIGHT.VOLUMETRIC_FACTOR;

    // Use whichever is higher: actual weight or volumetric weight
    return {
      actualWeight: totalWeight,
      volumetricWeight: volumetricWeight,
      chargeableWeight: Math.max(totalWeight, volumetricWeight),
    };
  };

  // Get incoterm from booking
  const getIncoterm = (booking: Booking) => {
    if (!booking.booking_incoterm_info) return null;

    return Array.isArray(booking.booking_incoterm_info)
      ? booking.booking_incoterm_info[0]
      : booking.booking_incoterm_info;
  };

  // Get container reference from booking
  const getContainerReference = (booking: Booking) => {
    if (!booking.container_info) return null;

    const containerInfo = Array.isArray(booking.container_info)
      ? booking.container_info[0]
      : booking.container_info;

    return containerInfo?.reference || null;
  };

  // Add function to fetch the company's payment term

  // Fetch container sizes from Supabase
  const fetchContainerSizes = async () => {
    try {
      const { data, error } = await typedSupabase
        .from("container_size")
        .select("*")
        .order("name");

      if (error) {
        throw error;
      }

      if (data) {
        setContainerSizes(data);
      }
    } catch (error) {
      console.error("Error fetching container sizes:", error);
      message.error("Failed to load container sizes");
    }
  };

  // Add function to add customs lines to invoice
  const addCustomsToInvoiceLines = (line: {
    description: string;
    quantity: number;
    unit_price: number;
    vat_percentage: number;
    cost_type: string;
  }) => {
    try {
      const lines = form.getFieldValue("lines");
      const newLine = {
        description: line.description,
        quantity: line.quantity,
        unit_price: Math.round(line.unit_price * 100) / 100, // Ensure rounding in the form field too
        vat_percentage: line.vat_percentage,
        cost_type: line.cost_type,
      };

      const updatedLines = [...(lines || []), newLine];
      form.setFieldValue("lines", updatedLines);
      const newFormValues = {
        ...formValues,
        lines: updatedLines,
      };
      setFormValues(newFormValues);

      // Auto-save immediately after adding a customs line
      if (isEditMode && editInvoice) {
        autoSaveInvoice(newFormValues);
      }
    } catch (error) {
      console.error("Error adding customs line to invoice:", error);
      message.error("Failed to add customs line to invoice");
    }
  };

  // Add function to add LMD lines to invoice
  const addLMDToInvoiceLines = (line: {
    description: string;
    quantity: number;
    unit_price: number;
    vat_percentage: number;
    cost_type: string;
  }) => {
    try {
      const lines = form.getFieldValue("lines");
      const newLine = {
        description: line.description,
        quantity: line.quantity,
        unit_price: Math.round(line.unit_price * 100) / 100, // Ensure rounding in the form field too
        vat_percentage: line.vat_percentage,
        cost_type: line.cost_type,
      };

      const updatedLines = [...(lines || []), newLine];
      form.setFieldValue("lines", updatedLines);
      const newFormValues = {
        ...formValues,
        lines: updatedLines,
      };
      setFormValues(newFormValues);

      // Auto-save immediately after adding a LMD line
      if (isEditMode && editInvoice) {
        autoSaveInvoice(newFormValues);
      }
    } catch (error) {
      console.error("Error adding LMD line to invoice:", error);
      message.error("Failed to add LMD line to invoice");
    }
  };

  // Add function to add tariff to invoice lines
  const addTariffToInvoiceLines = async () => {
    if (!selectedBooking) {
      message.error("No booking selected");
      return;
    }

    try {
      setLoading(true);

      // Calculate quantity based on tariff calculation type
      let quantity = 1;
      let description = "Shipment Fee (Tariff)";

      // Use manual quantity if set, otherwise calculate automatically
      if (
        tariffRow.use_manual_quantity &&
        tariffRow.manual_quantity !== undefined
      ) {
        quantity = tariffRow.manual_quantity;
        if (tariffCalculationType === "per_kg") {
          description = `Air Freight Tariff (${quantity.toFixed(
            2
          )} kg - Manual)`;
        } else if (tariffCalculationType === "per_cbm") {
          description = `Sea Freight Tariff (${quantity.toFixed(
            2
          )} m³ - Manual)`;
        } else {
          const containerTypeName = tariffRow.container_type_id
            ? containerSizes.find(
                (size) => size.id === tariffRow.container_type_id
              )?.name || "Container"
            : "Container";
          description = `Sea Freight Tariff (${quantity} ${containerTypeName} - Manual)`;
        }
      } else {
        // Auto-calculate quantity
        if (tariffCalculationType === "per_kg") {
          const { chargeableWeight } =
            calculateChargeableWeight(selectedBooking);
          quantity = chargeableWeight;
          description = `Air Freight Tariff (${quantity.toFixed(2)} kg)`;
        } else if (tariffCalculationType === "per_cbm") {
          quantity = getTotalVolumeFromBooking(selectedBooking);
          description = `Sea Freight Tariff (${quantity.toFixed(2)} m³)`;
        } else if (tariffCalculationType === "per_container") {
          quantity = 1;
          const containerTypeName = tariffRow.container_type_id
            ? containerSizes.find(
                (size) => size.id === tariffRow.container_type_id
              )?.name || "Container"
            : "Container";
          description = `Sea Freight Tariff (${containerTypeName})`;
        }
      }

      const lines = form.getFieldValue("lines");
      const newLine = {
        description,
        quantity,
        unit_price: tariffRow.unit_price,
        vat_percentage: tariffRow.vat_percentage,
        cost_type: "tariff",
      };

      const updatedLines = [...(lines || []), newLine];
      form.setFieldValue("lines", updatedLines);
      const newFormValues = {
        ...formValues,
        lines: updatedLines,
      };
      setFormValues(newFormValues);

      message.success("Tariff added to invoice lines successfully");

      // Auto-save immediately after adding a tariff line
      if (isEditMode && editInvoice) {
        autoSaveInvoice(newFormValues);
      }

      // Reset tariff row
      setTariffRow({
        unit_price: 0,
        vat_percentage: 0,
        use_manual_quantity: false,
        manual_quantity: undefined,
      });
    } catch (error) {
      console.error("Error adding tariff to invoice lines:", error);
      message.error("Failed to add tariff to invoice lines");
    } finally {
      setLoading(false);
    }
  };

  // Add function to auto-save the invoice
  const autoSaveInvoice = async (values: FormValues) => {
    // Only auto-save in edit mode when we have an existing invoice
    if (!isEditMode || !editInvoice || !selectedBooking) {
      return;
    }

    try {
      setAutoSaving(true);

      const invoiceData: Partial<Invoice> = {
        booking_id: values.booking_id!,
        company_id: selectedBooking?.company_info
          ? Array.isArray(selectedBooking.company_info)
            ? selectedBooking.company_info[0]?.id
            : selectedBooking.company_info.id
          : undefined,
        currency: values.currency,
        payment_term: values.payment_term,
        custom_payment_terms: values.custom_payment_terms,
        lines: values.lines.map((line) => ({
          id: line.id,
          description: line.description,
          quantity: line.quantity || 0,
          unit_price: line.unit_price || 0,
          vat_percentage: line.vat_percentage || 0,
          cost_type: line.cost_type,
        })),
      };

      const { error } = await updateInvoice(editInvoice.id!, invoiceData);

      if (error) {
        console.error("Auto-save error:", error);
        return;
      }

      setLastSaved(new Date());
    } catch (error) {
      console.error("Auto-save error:", error);
    } finally {
      setAutoSaving(false);
    }
  };

  // Add function to find tariff lines in the current invoice
  const getTariffLines = () => {
    const lines = form.getFieldValue("lines") || [];
    return lines
      .map((line: FormValues["lines"][0], index: number) => ({
        ...line,
        index,
      }))
      .filter(
        (line: FormValues["lines"][0] & { index: number }) =>
          line.cost_type === "tariff"
      );
  };

  // Add function to find customs lines in the current invoice
  const getCustomsLines = () => {
    const lines = form.getFieldValue("lines") || [];
    return lines
      .map((line: FormValues["lines"][0], index: number) => ({
        ...line,
        index,
      }))
      .filter(
        (line: FormValues["lines"][0] & { index: number }) =>
          line.cost_type === "customs_btw" ||
          line.cost_type === "customs_duties" ||
          line.cost_type === "customs_commission"
      );
  };

  // Add function to find LMD lines in the current invoice
  const getLMDLines = () => {
    const lines = form.getFieldValue("lines") || [];
    return lines
      .map((line: FormValues["lines"][0], index: number) => ({
        ...line,
        index,
      }))
      .filter(
        (line: FormValues["lines"][0] & { index: number }) =>
          line.cost_type === "lmd_delivery"
      );
  };

  // Add function to remove tariff line
  const removeTariffLine = (lineIndex: number) => {
    const lines = form.getFieldValue("lines") || [];
    const updatedLines = lines.filter(
      (_: FormValues["lines"][0], index: number) => index !== lineIndex
    );
    form.setFieldValue("lines", updatedLines);
    const newFormValues = {
      ...formValues,
      lines: updatedLines,
    };
    setFormValues(newFormValues);

    // Auto-save immediately after removing a line
    if (isEditMode && editInvoice) {
      autoSaveInvoice(newFormValues);
    }

    message.success("Tariff line removed successfully");
  };

  // Add function to remove customs line
  const removeCustomsLine = (lineIndex: number) => {
    const lines = form.getFieldValue("lines") || [];
    const updatedLines = lines.filter(
      (_: FormValues["lines"][0], index: number) => index !== lineIndex
    );
    form.setFieldValue("lines", updatedLines);
    const newFormValues = {
      ...formValues,
      lines: updatedLines,
    };
    setFormValues(newFormValues);

    // Auto-save immediately after removing a line
    if (isEditMode && editInvoice) {
      autoSaveInvoice(newFormValues);
    }

    message.success("Customs line removed successfully");
  };

  // Add function to remove LMD line
  const removeLMDLine = (lineIndex: number) => {
    const lines = form.getFieldValue("lines") || [];
    const updatedLines = lines.filter(
      (_: FormValues["lines"][0], index: number) => index !== lineIndex
    );
    form.setFieldValue("lines", updatedLines);
    const newFormValues = {
      ...formValues,
      lines: updatedLines,
    };
    setFormValues(newFormValues);

    // Auto-save immediately after removing a line
    if (isEditMode && editInvoice) {
      autoSaveInvoice(newFormValues);
    }

    message.success("LMD line removed successfully");
  };

  // Add function to edit tariff line
  const editTariffLine = (lineIndex: number) => {
    const lines = form.getFieldValue("lines") || [];
    const tariffLine = lines[lineIndex];

    if (tariffLine && tariffLine.cost_type === "tariff") {
      // Parse the description to determine calculation type
      let calculationType: "per_kg" | "per_cbm" | "per_container" = "per_kg";
      if (
        tariffLine.description.includes("Sea Freight") &&
        tariffLine.description.includes("m³")
      ) {
        calculationType = "per_cbm";
      } else if (
        tariffLine.description.includes("Sea Freight") &&
        !tariffLine.description.includes("m³")
      ) {
        calculationType = "per_container";
      }

      // Set the tariff row state with the existing values
      setTariffCalculationType(calculationType);
      setTariffRow({
        unit_price: tariffLine.unit_price || 0,
        vat_percentage: tariffLine.vat_percentage || 0,
        manual_quantity: tariffLine.quantity,
        use_manual_quantity: true,
      });

      // Remove the line so it can be re-added with new values
      removeTariffLine(lineIndex);

      message.info(
        "Tariff loaded for editing. Modify values and click 'Add Tariff to Invoice Lines' to save changes."
      );
    }
  };

  // Add function to fetch tariff suggestions based on booking details
  const fetchTariffSuggestions = async () => {
    if (!selectedBooking) return;

    try {
      setLoadingSuggestions(true);
      setSelectedTariffId(null); // Clear previous selection

      // Get the current date or container ETD date for tariff matching
      const dateToMatch = (() => {
        if (selectedBooking.container_info) {
          const containerInfo = Array.isArray(selectedBooking.container_info)
            ? selectedBooking.container_info[0]
            : selectedBooking.container_info;

          if (containerInfo?.etd) {
            return containerInfo.etd;
          }
        }
        return new Date().toISOString().split("T")[0];
      })();

      // Get origin and destination ports (prioritize container ports)
      let originPort = selectedBooking.origin_port;
      let destinationPort = selectedBooking.destination_port;

      if (selectedBooking.container_info) {
        const containerInfo = Array.isArray(selectedBooking.container_info)
          ? selectedBooking.container_info[0]
          : selectedBooking.container_info;

        if (containerInfo?.origin_port) {
          const containerOriginPort = Array.isArray(containerInfo.origin_port)
            ? containerInfo.origin_port[0]
            : containerInfo.origin_port;

          if (containerOriginPort?.id) {
            originPort = containerOriginPort.id;
          }
        }

        if (containerInfo?.destination_port) {
          const containerDestPort = Array.isArray(
            containerInfo.destination_port
          )
            ? containerInfo.destination_port[0]
            : containerInfo.destination_port;

          if (containerDestPort?.id) {
            destinationPort = containerDestPort.id;
          }
        }
      }

      // Get transport type ID
      const transportTypeId = selectedBooking.transport_type_info
        ? Array.isArray(selectedBooking.transport_type_info)
          ? selectedBooking.transport_type_info[0]?.id
          : selectedBooking.transport_type_info.id
        : null;

      if (!transportTypeId || !originPort || !destinationPort) {
        setSuggestedTariffs([]);
        return;
      }

      // Query tariffs table for matching tariffs (simplified)
      const { data: matchingTariffs, error } = await typedSupabase
        .from("tariffs")
        .select(
          `
          id, 
          price, 
          transport_type,
          origin_port,
          destination_port,
          bookings_type,
          container_size,
          tariff_bracket,
          from,
          to
        `
        )
        .eq("transport_type", transportTypeId)
        .eq("origin_port", originPort)
        .eq("destination_port", destinationPort)
        .lte("from", dateToMatch)
        .or(`to.gte.${dateToMatch},to.is.null`)
        .order("price", { ascending: true });

      console.log("Tariff query params:", {
        transportTypeId,
        originPort,
        destinationPort,
        dateToMatch,
      });
      console.log("Tariff query result:", { matchingTariffs, error });

      // No tariffs found - don't show any dummy data
      if (!matchingTariffs || matchingTariffs.length === 0) {
        console.log("No tariffs found");
        setSuggestedTariffs([]);
        return;
      }

      if (error) {
        console.error("Error fetching tariff suggestions:", error);
        setSuggestedTariffs([]);
        return;
      }

      if (matchingTariffs && matchingTariffs.length > 0) {
        // Get bracket information for tariffs that have brackets
        const bracketIds = matchingTariffs
          .filter((t) => t.tariff_bracket)
          .map((t) => t.tariff_bracket);

        let bracketInfo: Record<
          number,
          {
            id: number;
            from: number;
            to: number;
            transport_type: number | null;
          }
        > = {};
        if (bracketIds.length > 0) {
          const { data: brackets } = await typedSupabase
            .from("tariff_brackets")
            .select("id, from, to, transport_type")
            .in("id", bracketIds);

          if (brackets) {
            bracketInfo = brackets.reduce((acc, bracket) => {
              acc[bracket.id] = bracket;
              return acc;
            }, {} as Record<number, { id: number; from: number; to: number; transport_type: number | null }>);
          }
        }

        // Get booking type and container size names
        const bookingTypeIds = [
          ...new Set(
            matchingTariffs
              .filter((t) => t.bookings_type)
              .map((t) => t.bookings_type)
          ),
        ];
        const containerSizeIds = [
          ...new Set(
            matchingTariffs
              .filter((t) => t.container_size)
              .map((t) => t.container_size)
          ),
        ];

        let bookingTypeInfo: Record<number, { id: number; name: string }> = {};
        let containerSizeInfo: Record<number, { id: number; name: string }> =
          {};

        if (bookingTypeIds.length > 0) {
          const { data: bookingTypes } = await typedSupabase
            .from("bookings_type")
            .select("id, name")
            .in("id", bookingTypeIds);

          if (bookingTypes) {
            bookingTypeInfo = bookingTypes.reduce((acc, bt) => {
              acc[bt.id] = bt;
              return acc;
            }, {} as Record<number, { id: number; name: string }>);
          }
        }

        if (containerSizeIds.length > 0) {
          const { data: containerSizes } = await typedSupabase
            .from("container_size")
            .select("id, name")
            .in("id", containerSizeIds);

          if (containerSizes) {
            containerSizeInfo = containerSizes.reduce((acc, cs) => {
              acc[cs.id] = cs;
              return acc;
            }, {} as Record<number, { id: number; name: string }>);
          }
        }

        // Get booking characteristics for smart matching
        const bookingType = selectedBooking.booking_type_info
          ? Array.isArray(selectedBooking.booking_type_info)
            ? selectedBooking.booking_type_info[0]?.id
            : selectedBooking.booking_type_info.id
          : null;

        const bookingVolume = getTotalVolumeFromBooking(selectedBooking);
        const bookingContainerSize = (() => {
          // For now, we can't get container size from container_info
          // as it's not included in the query. This could be enhanced later.
          return null;
        })();

        console.log("Booking characteristics for smart matching:", {
          bookingType,
          bookingVolume,
          bookingContainerSize,
          transportTypeId,
          isLCLBooking: bookingType === 1,
          availableBrackets:
            Object.keys(bracketInfo).length > 0 ? bracketInfo : "none",
          selectedBookingInfo: {
            booking_type_info: selectedBooking.booking_type_info,
            total_volume: getTotalVolumeFromBooking(selectedBooking),
          },
        });

        // Smart sort tariffs by relevance
        const sortedTariffs = [...matchingTariffs].sort((a, b) => {
          let scoreA = 0;
          let scoreB = 0;

          // Priority 1: Exact booking type match (FCL/LCL)
          if (bookingType) {
            if (a.bookings_type === bookingType) scoreA += 100;
            if (b.bookings_type === bookingType) scoreB += 100;
          }

          // Priority 2: Enhanced volume bracket match - especially important for LCL (booking_type = 1)
          const isLCLBooking = bookingType === 1; // LCL booking type
          if (bookingVolume > 0) {
            if (a.tariff_bracket && bracketInfo[a.tariff_bracket]) {
              const bracketA = bracketInfo[a.tariff_bracket];
              const volumeMatchesA =
                bookingVolume >= bracketA.from && bookingVolume <= bracketA.to;
              const transportMatchesA =
                !bracketA.transport_type ||
                bracketA.transport_type === transportTypeId;
              if (volumeMatchesA && transportMatchesA) {
                // Give higher score for LCL bookings with exact volume bracket match
                scoreA += isLCLBooking ? 80 : 50;

                // Bonus for exact volume fit (closer to middle of bracket range)
                const bracketRange = bracketA.to - bracketA.from;
                const volumePosition =
                  (bookingVolume - bracketA.from) / bracketRange;
                const centerBonus = 1 - Math.abs(0.5 - volumePosition); // 0-1, higher when closer to center
                scoreA += isLCLBooking ? centerBonus * 20 : centerBonus * 10;
              }
            }

            if (b.tariff_bracket && bracketInfo[b.tariff_bracket]) {
              const bracketB = bracketInfo[b.tariff_bracket];
              const volumeMatchesB =
                bookingVolume >= bracketB.from && bookingVolume <= bracketB.to;
              const transportMatchesB =
                !bracketB.transport_type ||
                bracketB.transport_type === transportTypeId;
              if (volumeMatchesB && transportMatchesB) {
                // Give higher score for LCL bookings with exact volume bracket match
                scoreB += isLCLBooking ? 80 : 50;

                // Bonus for exact volume fit (closer to middle of bracket range)
                const bracketRange = bracketB.to - bracketB.from;
                const volumePosition =
                  (bookingVolume - bracketB.from) / bracketRange;
                const centerBonus = 1 - Math.abs(0.5 - volumePosition); // 0-1, higher when closer to center
                scoreB += isLCLBooking ? centerBonus * 20 : centerBonus * 10;
              }
            }
          }

          // Priority 3: Container size match for FCL bookings (less important for LCL)
          if (bookingContainerSize && !isLCLBooking) {
            if (a.container_size === bookingContainerSize) scoreA += 30;
            if (b.container_size === bookingContainerSize) scoreB += 30;
          }

          // Priority 4: Prefer tariffs with brackets for LCL, without brackets for FCL
          if (isLCLBooking) {
            if (a.tariff_bracket) scoreA += 15;
            if (b.tariff_bracket) scoreB += 15;
          } else {
            if (!a.tariff_bracket) scoreA += 10;
            if (!b.tariff_bracket) scoreB += 10;
          }

          // Priority 5: Has no specific restrictions (more flexible) - but lower priority for LCL
          if (!a.bookings_type && !a.container_size && !a.tariff_bracket) {
            scoreA += isLCLBooking ? 5 : 10;
          }
          if (!b.bookings_type && !b.container_size && !b.tariff_bracket) {
            scoreB += isLCLBooking ? 5 : 10;
          }

          // Priority 6: Lower price (minor factor)
          scoreA += (1000 - (a.price || 0)) / 1000;
          scoreB += (1000 - (b.price || 0)) / 1000;

          return scoreB - scoreA; // Sort by score descending
        });

        // Format suggestions with proper descriptions and smart badges
        const suggestions = sortedTariffs.map((tariff, index) => {
          let description = `Option ${index + 1}`;
          let isSmartMatch = false;

          // Check if this is a smart match
          const matchReasons: string[] = [];
          const isLCLBooking = bookingType === 1; // LCL booking type

          if (bookingType && tariff.bookings_type === bookingType) {
            matchReasons.push("booking type");
            isSmartMatch = true;
          }

          if (
            bookingVolume > 0 &&
            tariff.tariff_bracket &&
            bracketInfo[tariff.tariff_bracket]
          ) {
            const bracket = bracketInfo[tariff.tariff_bracket];
            const volumeMatches =
              bookingVolume >= bracket.from && bookingVolume <= bracket.to;

            // Also check if transport type matches (if specified in bracket)
            const transportTypeMatches =
              !bracket.transport_type ||
              bracket.transport_type === transportTypeId;

            console.log(`Tariff ${tariff.id} bracket check:`, {
              bookingVolume,
              bracket,
              volumeMatches,
              transportTypeMatches,
              bookingTransportType: transportTypeId,
              isLCLBooking,
            });

            if (volumeMatches && transportTypeMatches) {
              matchReasons.push("volume bracket");
              isSmartMatch = true;

              // For LCL bookings, add additional detail about volume fit
              if (isLCLBooking) {
                const bracketRange = bracket.to - bracket.from;
                const volumePosition =
                  (bookingVolume - bracket.from) / bracketRange;
                if (volumePosition >= 0.3 && volumePosition <= 0.7) {
                  matchReasons.push("optimal volume");
                }
              }
            }
          }

          if (
            bookingContainerSize &&
            tariff.container_size === bookingContainerSize
          ) {
            matchReasons.push("container size");
            isSmartMatch = true;
          }

          // Add smart match indicator
          if (isSmartMatch) {
            description += ` ✨ RECOMMENDED`;

            // For LCL with volume bracket match, add special indicator
            if (isLCLBooking && matchReasons.includes("volume bracket")) {
              description += ` (${bookingVolume.toFixed(1)} CBM fit)`;
            }
          }

          // Add booking type name
          if (tariff.bookings_type && bookingTypeInfo[tariff.bookings_type]) {
            description += ` - ${bookingTypeInfo[tariff.bookings_type].name}`;
          }

          // Add container size name
          if (
            tariff.container_size &&
            containerSizeInfo[tariff.container_size]
          ) {
            description += ` (${
              containerSizeInfo[tariff.container_size].name
            })`;
          }

          // Separate bracket info for better display
          let bracketDisplay = null;
          if (tariff.tariff_bracket && bracketInfo[tariff.tariff_bracket]) {
            const bracket = bracketInfo[tariff.tariff_bracket];
            bracketDisplay = `${bracket.from}-${bracket.to} CBM`;
            description += ` [${bracketDisplay}]`;
          }

          return {
            id: tariff.id,
            price: tariff.price || 0,
            description,
            from: tariff.from,
            to: tariff.to,
            isSmartMatch,
            matchReasons,
            bracketDisplay,
            bookingType:
              tariff.bookings_type && bookingTypeInfo[tariff.bookings_type]
                ? bookingTypeInfo[tariff.bookings_type].name
                : null,
            containerSize:
              tariff.container_size && containerSizeInfo[tariff.container_size]
                ? containerSizeInfo[tariff.container_size].name
                : null,
          };
        });

        console.log("Smart-sorted tariff suggestions:", suggestions);
        setSuggestedTariffs(suggestions);

        // Auto-select the first (highest-scoring) smart match
        if (suggestions.length > 0 && suggestions[0].isSmartMatch) {
          console.log("Auto-selecting best smart match:", suggestions[0]);
          setSelectedTariffId(suggestions[0].id);
          setTariffRow((prev) => ({
            ...prev,
            unit_price: suggestions[0].price,
          }));
        }
      } else {
        setSuggestedTariffs([]);
      }
    } catch (error) {
      console.error("Error fetching tariff suggestions:", error);
      setSuggestedTariffs([]);
    } finally {
      setLoadingSuggestions(false);
    }
  };

  // Add function to auto-save individual invoice line
  const autoSaveInvoiceLine = async (
    lineIndex: number,
    lineData: FormValues["lines"][0]
  ) => {
    // Only auto-save in edit mode when we have an existing invoice
    if (!isEditMode || !editInvoice || !selectedBooking) {
      return;
    }

    try {
      setSavingLineIndex(lineIndex);

      // Get current form values
      const currentLines = form.getFieldValue("lines") || [];

      // Update the specific line
      currentLines[lineIndex] = lineData;

      const invoiceData: Partial<Invoice> = {
        booking_id: formValues.booking_id!,
        company_id: selectedBooking?.company_info
          ? Array.isArray(selectedBooking.company_info)
            ? selectedBooking.company_info[0]?.id
            : selectedBooking.company_info.id
          : undefined,
        currency: formValues.currency,
        payment_term: formValues.payment_term,
        custom_payment_terms: formValues.custom_payment_terms,
        lines: currentLines.map((line: FormValues["lines"][0]) => ({
          id: line.id,
          description: line.description,
          quantity: line.quantity || 0,
          unit_price: line.unit_price || 0,
          vat_percentage: line.vat_percentage || 0,
          cost_type: line.cost_type,
        })),
      };

      const { error } = await updateInvoice(editInvoice.id!, invoiceData);

      if (error) {
        console.error("Auto-save line error:", error);
        return;
      }

      setLastSaved(new Date());
    } catch (error) {
      console.error("Auto-save line error:", error);
    } finally {
      setSavingLineIndex(null);
    }
  };

  return (
    <>
      <Card
        title={
          <div
            style={{
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
            }}
          >
            <span>{isEditMode ? "Edit Invoice" : "Create Invoice"}</span>
            <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
              <Button
                type="default"
                onClick={() => setPreviewModalVisible(true)}
                disabled={!selectedBooking}
              >
                Preview Invoice
              </Button>
              {isEditMode && (
                <div style={{ fontSize: "12px", color: "#666" }}>
                  {autoSaving ? (
                    <span style={{ color: "#1890ff" }}>
                      <LoadingOutlined style={{ marginRight: 4 }} />
                      Saving...
                    </span>
                  ) : lastSaved ? (
                    <span style={{ color: "#52c41a" }}>
                      ✓ Saved{" "}
                      {(() => {
                        const secondsAgo = getSecondsAgo(lastSaved);
                        if (secondsAgo < 60) {
                          return `${secondsAgo} seconds ago`;
                        } else if (secondsAgo < 3600) {
                          return `${Math.floor(secondsAgo / 60)} minutes ago`;
                        } else {
                          return lastSaved.toLocaleTimeString();
                        }
                      })()}
                    </span>
                  ) : null}
                </div>
              )}
            </div>
          </div>
        }
        size="small"
        style={{ width: "100%" }}
      >
        <Form
          form={form}
          layout="vertical"
          onFinish={handleSubmit}
          initialValues={{
            payment_term: PaymentTerm.DAYS_30,
            lines: [
              {
                description: "",
                quantity: 1,
                unit_price: 0,
                vat_percentage: 0,
              },
            ],
          }}
          onValuesChange={handleValuesChange}
          size="small"
        >
          <BookingSelector
            bookings={bookings}
            onBookingChange={handleBookingChange}
            isEditMode={isEditMode}
            getBookingDisplayName={getBookingDisplayName}
          />

          {isEditMode && (
            <Form.Item name="invoice_number" label="Invoice Number">
              <Input disabled />
            </Form.Item>
          )}

          <PaymentTermsSection
            selectedBooking={selectedBooking}
            paymentTerm={formValues.payment_term}
            customPaymentTerms={formValues.custom_payment_terms}
          />

          {/* Hidden form item to store the payment term value */}
          {selectedBooking && (
            <Form.Item name="payment_term" hidden>
              <Input type="hidden" />
            </Form.Item>
          )}

          {selectedBooking && (
            <Card size="small" style={{ marginBottom: 8 }} title={null}>
              <Row gutter={8}>
                {/* Column 1: Reference & Basic Info */}
                <Col span={8}>
                  <Text style={{ fontSize: "11px" }}>
                    <Text strong>Reference:</Text>{" "}
                    {selectedBooking.reference || `EFF${selectedBooking.id}`}
                    {selectedBooking.transport_type_info &&
                      (Array.isArray(selectedBooking.transport_type_info)
                        ? selectedBooking.transport_type_info[0]?.abbreviation
                        : selectedBooking.transport_type_info.abbreviation)}
                    <br />
                    <Text strong>Transport:</Text>{" "}
                    {selectedBooking.transport_type_info &&
                      (Array.isArray(selectedBooking.transport_type_info)
                        ? selectedBooking.transport_type_info[0]?.name
                        : selectedBooking.transport_type_info.name)}
                    <br />
                    {(() => {
                      // Show booking type for seafreight (transport_type 2)
                      const transportTypeId =
                        selectedBooking.transport_type_info
                          ? Array.isArray(selectedBooking.transport_type_info)
                            ? selectedBooking.transport_type_info[0]?.id
                            : selectedBooking.transport_type_info.id
                          : null;

                      if (
                        transportTypeId === 2 &&
                        selectedBooking.booking_type_info
                      ) {
                        const bookingType = Array.isArray(
                          selectedBooking.booking_type_info
                        )
                          ? selectedBooking.booking_type_info[0]?.name
                          : selectedBooking.booking_type_info.name;

                        if (bookingType) {
                          return (
                            <>
                              <Text strong>Booking Type:</Text> {bookingType}
                              <br />
                            </>
                          );
                        }
                      }
                      return null;
                    })()}
                    <Text strong>Company:</Text>{" "}
                    {selectedBooking.company_info &&
                      (Array.isArray(selectedBooking.company_info)
                        ? selectedBooking.company_info[0]?.name
                        : selectedBooking.company_info.name)}
                    <br />
                    {(() => {
                      const incoterm = getIncoterm(selectedBooking);
                      if (incoterm) {
                        return (
                          <>
                            <Text strong>Incoterm:</Text> {incoterm.name}
                            <br />
                          </>
                        );
                      }
                      return null;
                    })()}
                  </Text>
                </Col>

                {/* Column 2: Location & Container Info */}
                <Col span={8}>
                  <Text style={{ fontSize: "11px" }}>
                    <Text strong>Origin:</Text>{" "}
                    {(() => {
                      const originInfo = getOriginPort(selectedBooking);
                      return (
                        <>
                          {originInfo.name}
                          {originInfo.isFromContainer && (
                            <Tooltip
                              title={`From container ${
                                originInfo.containerRef || ""
                              }`}
                            >
                              <Tag
                                color="blue"
                                style={{ marginLeft: 4, fontSize: "9px" }}
                              >
                                Container
                              </Tag>
                            </Tooltip>
                          )}
                        </>
                      );
                    })()}
                    <br />
                    <Text strong>Destination:</Text>{" "}
                    {(() => {
                      const destInfo = getDestinationPort(selectedBooking);
                      return (
                        <>
                          {destInfo.name}
                          {destInfo.isFromContainer && (
                            <Tooltip
                              title={`From container ${
                                destInfo.containerRef || ""
                              }`}
                            >
                              <InfoCircleOutlined style={{ marginLeft: 4 }} />
                            </Tooltip>
                          )}
                        </>
                      );
                    })()}
                    <br />
                    {(() => {
                      const containerRef =
                        getContainerReference(selectedBooking);
                      if (containerRef) {
                        return (
                          <>
                            <Text strong>Container:</Text> {containerRef}
                            <br />
                          </>
                        );
                      }
                      return null;
                    })()}
                    {(() => {
                      if (!selectedBooking.container_info) return null;
                      const container = Array.isArray(
                        selectedBooking.container_info
                      )
                        ? selectedBooking.container_info[0]
                        : selectedBooking.container_info;
                      return (
                        <>
                          {container?.etd && (
                            <>
                              <Text strong>ETD:</Text>{" "}
                              {dayjs(container.etd).format("YYYY-MM-DD")}
                              <br />
                            </>
                          )}
                          {container?.eta && (
                            <>
                              <Text strong>ETA:</Text>{" "}
                              {dayjs(container.eta).format("YYYY-MM-DD")}
                              <br />
                            </>
                          )}
                        </>
                      );
                    })()}
                  </Text>
                </Col>

                {/* Column 3: Measurements & Products */}
                <Col span={8}>
                  <Text style={{ fontSize: "11px" }}>
                    <Text strong>Weight:</Text>{" "}
                    {getTotalWeightFromBooking(selectedBooking).toFixed(2)} kg
                    <br />
                    <Text strong>Volume:</Text>{" "}
                    {getTotalVolumeFromBooking(selectedBooking).toFixed(2)} m³
                    <br />
                    {(() => {
                      const isAirFreight =
                        selectedBooking.transport_type_info &&
                        (Array.isArray(selectedBooking.transport_type_info)
                          ? selectedBooking.transport_type_info[0]?.id === 1
                          : selectedBooking.transport_type_info.id === 1);

                      if (isAirFreight) {
                        const { chargeableWeight } =
                          calculateChargeableWeight(selectedBooking);
                        return (
                          <>
                            <Text strong>Chargeable:</Text>{" "}
                            <Text type="danger">
                              {chargeableWeight.toFixed(2)} kg
                            </Text>
                            <Tooltip title="For air freight, we use the higher of actual weight and volumetric weight (calculated as 1 CBM = 167 kg)">
                              <InfoCircleOutlined
                                style={{
                                  marginLeft: 4,
                                  fontSize: "9px",
                                }}
                              />
                            </Tooltip>
                            <br />
                          </>
                        );
                      }
                      return null;
                    })()}
                    <Text strong>Products:</Text>{" "}
                    {selectedBooking.product_info
                      ? Array.isArray(selectedBooking.product_info[0])
                        ? selectedBooking.product_info[0].length
                        : selectedBooking.product_info.length
                      : 0}{" "}
                    items
                    <br />
                  </Text>
                </Col>
              </Row>
            </Card>
          )}

          <Divider orientation="left" style={{ margin: "12px 0" }}>
            Invoice Lines
            {autogeneratingLines && (
              <LoadingOutlined style={{ marginLeft: 8 }} />
            )}
            <Tooltip title="Invoice lines are generated based on matching pricing rules. The system considers transport type, origin/destination ports, and calculates quantities based on weight, volume, or product count from the booking.">
              <InfoCircleOutlined
                style={{ marginLeft: 8, cursor: "pointer" }}
                onClick={() => setPricingInfoVisible(true)}
              />
            </Tooltip>
          </Divider>

          {/* Dedicated Tariff Row - Show when booking is selected */}
          {selectedBooking && (
            <Card
              size="small"
              style={{
                marginBottom: 12,
                border: "2px solid #1890ff",
                backgroundColor: "#f0f8ff",
              }}
              title={
                <span
                  style={{
                    color: "#1890ff",
                    fontWeight: "bold",
                    fontSize: "14px",
                  }}
                >
                  🚢 Tariff Calculation
                </span>
              }
              bodyStyle={{ padding: "12px" }}
            >
              <Row gutter={8}>
                <Col span={6}>
                  <Form.Item label="Transport Type" style={{ marginBottom: 4 }}>
                    <Select
                      value={tariffCalculationType}
                      onChange={(value) => setTariffCalculationType(value)}
                      style={{ width: "100%" }}
                      size="small"
                    >
                      {(() => {
                        const isAirFreight =
                          selectedBooking.transport_type_info &&
                          (Array.isArray(selectedBooking.transport_type_info)
                            ? selectedBooking.transport_type_info[0]?.id === 1
                            : selectedBooking.transport_type_info.id === 1);

                        if (isAirFreight) {
                          return (
                            <Option value="per_kg">
                              ✈️ Air Freight (per KG)
                            </Option>
                          );
                        } else {
                          return [
                            <Option key="per_cbm" value="per_cbm">
                              🚢 Sea Freight (per CBM)
                            </Option>,
                            <Option key="per_container" value="per_container">
                              📦 Sea Freight (per Container)
                            </Option>,
                          ];
                        }
                      })()}
                    </Select>
                  </Form.Item>
                </Col>
                <Col span={6}>
                  {tariffCalculationType === "per_container" && (
                    <Form.Item
                      label="Container Type"
                      style={{ marginBottom: 4 }}
                    >
                      <Select
                        placeholder="Select container type"
                        style={{ width: "100%" }}
                        size="small"
                        value={tariffRow.container_type_id}
                        onChange={(value) => {
                          setTariffRow((prev) => ({
                            ...prev,
                            container_type_id: value,
                          }));

                          // Force re-render of suggested tariffs by creating a shallow copy
                          if (suggestedTariffs.length > 0) {
                            setSuggestedTariffs([...suggestedTariffs]);
                          }
                        }}
                      >
                        {containerSizes.map((size) => (
                          <Option key={size.id} value={size.id}>
                            {size.name}
                            {suggestedTariffs.length > 0 && (
                              <span
                                style={{
                                  marginLeft: "8px",
                                  fontSize: "11px",
                                  color: "#1890ff",
                                }}
                              >
                                (
                                {
                                  suggestedTariffs.filter(
                                    (t) => t.containerSize === size.name
                                  ).length
                                }
                                )
                              </span>
                            )}
                          </Option>
                        ))}
                      </Select>
                    </Form.Item>
                  )}
                </Col>
                <Col span={4}>
                  <Form.Item label="Quantity" style={{ marginBottom: 4 }}>
                    <InputNumber
                      value={(() => {
                        if (
                          tariffRow.use_manual_quantity &&
                          tariffRow.manual_quantity !== undefined
                        ) {
                          return tariffRow.manual_quantity;
                        }

                        if (tariffCalculationType === "per_kg") {
                          const { chargeableWeight } =
                            calculateChargeableWeight(selectedBooking);
                          return chargeableWeight;
                        } else if (tariffCalculationType === "per_cbm") {
                          return getTotalVolumeFromBooking(selectedBooking);
                        } else {
                          return 1; // per container
                        }
                      })()}
                      disabled={!tariffRow.use_manual_quantity}
                      onChange={(value) =>
                        setTariffRow((prev) => ({
                          ...prev,
                          manual_quantity: value || 0,
                        }))
                      }
                      style={{ width: "100%" }}
                      formatter={(value) => {
                        if (tariffCalculationType === "per_kg") {
                          return `${value} kg`;
                        } else if (tariffCalculationType === "per_cbm") {
                          return `${value} m³`;
                        } else {
                          return `${value}`;
                        }
                      }}
                      parser={(value) => {
                        const parsed = value
                          ? value.replace(/ (kg|m³)/g, "")
                          : "0";
                        return parseFloat(parsed) as unknown as 0;
                      }}
                    />
                  </Form.Item>
                </Col>
                <Col span={4}>
                  <Form.Item label="Unit Price" style={{ marginBottom: 4 }}>
                    <InputNumber
                      min={0}
                      step={0.01}
                      style={{ width: "100%" }}
                      size="small"
                      placeholder="Enter rate"
                      value={tariffRow.unit_price}
                      onFocus={(e) => {
                        // Auto-clear if value is 0
                        if (tariffRow.unit_price === 0) {
                          e.target.select();
                        }
                        // Fetch tariff suggestions if needed
                        if (selectedBooking && suggestedTariffs.length === 0) {
                          fetchTariffSuggestions();
                        }
                      }}
                      onChange={(value) =>
                        setTariffRow((prev) => ({
                          ...prev,
                          unit_price: value || 0,
                        }))
                      }
                      formatter={(value) =>
                        value
                          ? `€${value}`.replace(/\B(?=(\d{3})+(?!\d))/g, ",")
                          : "€0"
                      }
                      parser={(value) => {
                        const parsed = value
                          ? value.replace(/€\s?|(,*)/g, "")
                          : "0";
                        return parseFloat(parsed) as unknown as 0;
                      }}
                    />
                  </Form.Item>
                </Col>
                <Col span={2}>
                  <Form.Item label="VAT %" style={{ marginBottom: 4 }}>
                    <InputNumber
                      min={0}
                      max={100}
                      value={tariffRow.vat_percentage}
                      onChange={(value) =>
                        setTariffRow((prev) => ({
                          ...prev,
                          vat_percentage: value || 0,
                        }))
                      }
                      style={{ width: "100%" }}
                      size="small"
                    />
                  </Form.Item>
                </Col>
                <Col span={2}>
                  <Form.Item label="&nbsp;" style={{ marginBottom: 4 }}>
                    <Button
                      type="primary"
                      onClick={addTariffToInvoiceLines}
                      style={{ width: "100%" }}
                      size="small"
                      disabled={tariffRow.unit_price === 0}
                    >
                      Add
                    </Button>
                  </Form.Item>
                </Col>
              </Row>

              <Row gutter={8}>
                <Col span={12}>
                  <label style={{ fontSize: "11px", cursor: "pointer" }}>
                    <input
                      type="checkbox"
                      checked={tariffRow.use_manual_quantity}
                      onChange={(e) =>
                        setTariffRow((prev) => ({
                          ...prev,
                          use_manual_quantity: e.target.checked,
                          manual_quantity: e.target.checked
                            ? tariffCalculationType === "per_kg"
                              ? calculateChargeableWeight(selectedBooking)
                                  .chargeableWeight
                              : tariffCalculationType === "per_cbm"
                              ? getTotalVolumeFromBooking(selectedBooking)
                              : 1
                            : undefined,
                        }))
                      }
                      style={{ marginRight: 4 }}
                    />
                    Manual quantity override
                  </label>
                </Col>
                <Col span={12}>
                  <Text
                    strong
                    style={{
                      fontSize: "12px",
                      textAlign: "center",
                      display: "block",
                    }}
                  >
                    Total: €{" "}
                    {(() => {
                      let quantity = 1;

                      // Use manual quantity if set, otherwise calculate automatically
                      if (
                        tariffRow.use_manual_quantity &&
                        tariffRow.manual_quantity !== undefined
                      ) {
                        quantity = tariffRow.manual_quantity;
                      } else {
                        if (tariffCalculationType === "per_kg") {
                          const { chargeableWeight } =
                            calculateChargeableWeight(selectedBooking);
                          quantity = chargeableWeight;
                        } else if (tariffCalculationType === "per_cbm") {
                          quantity = getTotalVolumeFromBooking(selectedBooking);
                        }
                        // per_container remains 1
                      }

                      return (quantity * tariffRow.unit_price).toFixed(2);
                    })()}
                  </Text>
                </Col>
              </Row>

              {suggestedTariffs.length > 0 && (
                <Row style={{ marginTop: 8 }}>
                  <Col span={24}>
                    <div style={{ marginBottom: 8 }}>
                      <Text
                        strong
                        style={{ fontSize: "12px", color: "#1890ff" }}
                      >
                        💡 Suggested Tariffs ({suggestedTariffs.length})
                      </Text>
                    </div>
                    <div
                      style={{
                        maxHeight: "200px",
                        overflowY: "auto",
                        border: "1px solid #d9d9d9",
                        borderRadius: "6px",
                        padding: "8px",
                      }}
                    >
                      {loadingSuggestions ? (
                        <div style={{ textAlign: "center", padding: "20px" }}>
                          <Text type="secondary">Loading suggestions...</Text>
                        </div>
                      ) : (
                        <div
                          style={{
                            display: "grid",
                            gridTemplateColumns: "repeat(5, 1fr)",
                            gap: "6px",
                          }}
                        >
                          {suggestedTariffs.map((tariff) => {
                            const isSelected = selectedTariffId === tariff.id;
                            return (
                              <Card
                                key={tariff.id}
                                size="small"
                                hoverable
                                style={{
                                  cursor: "pointer",
                                  border: isSelected
                                    ? "2px solid #1890ff"
                                    : tariff.isSmartMatch
                                    ? "1px solid #52c41a"
                                    : "1px solid #e6f7ff",
                                  borderRadius: "6px",
                                  backgroundColor: isSelected
                                    ? "#e6f7ff"
                                    : tariff.isSmartMatch &&
                                      (tariffCalculationType !==
                                        "per_container" ||
                                        !tariffRow.container_type_id ||
                                        (tariff.containerSize &&
                                          isMatchingContainer(
                                            tariff.containerSize
                                          )))
                                    ? "#f6ffed"
                                    : "#fafafa",
                                  transition: "all 0.2s ease",
                                }}
                                bodyStyle={{ padding: "6px" }}
                                onClick={() => {
                                  setSelectedTariffId(tariff.id);
                                  setTariffRow((prev) => ({
                                    ...prev,
                                    unit_price: tariff.price,
                                  }));
                                }}
                              >
                                <div style={{ marginBottom: "4px" }}>
                                  <div
                                    style={{
                                      display: "flex",
                                      justifyContent: "space-between",
                                      alignItems: "flex-start",
                                      marginBottom: "2px",
                                    }}
                                  >
                                    <Text
                                      strong
                                      style={{
                                        fontSize: "16px",
                                        color: isSelected
                                          ? "#1890ff"
                                          : "#1890ff",
                                      }}
                                    >
                                      €{tariff.price.toFixed(2)}
                                    </Text>
                                    {tariff.isSmartMatch && (
                                      <span
                                        style={{
                                          fontSize: "10px",
                                          background: "#52c41a",
                                          color: "white",
                                          padding: "2px 5px",
                                          borderRadius: "8px",
                                          fontWeight: "bold",
                                        }}
                                      >
                                        ✨
                                      </span>
                                    )}
                                  </div>

                                  <div style={{ marginBottom: "3px" }}>
                                    {tariff.bookingType && (
                                      <Text
                                        style={{
                                          fontSize: "12px",
                                          fontWeight: "500",
                                          color: "#1890ff",
                                          display: "block",
                                        }}
                                      >
                                        {tariff.bookingType}
                                      </Text>
                                    )}

                                    {tariff.containerSize && (
                                      <Text
                                        style={{
                                          fontSize: "11px",
                                          color: isMatchingContainer(
                                            tariff.containerSize
                                          )
                                            ? "#52c41a"
                                            : "#666",
                                          display: "block",
                                          fontWeight: isMatchingContainer(
                                            tariff.containerSize
                                          )
                                            ? "bold"
                                            : "normal",
                                        }}
                                      >
                                        {isMatchingContainer(
                                          tariff.containerSize
                                        )
                                          ? "✓ "
                                          : ""}
                                        {tariff.containerSize}
                                      </Text>
                                    )}

                                    {tariff.bracketDisplay && (
                                      <div
                                        style={{
                                          background: "#e6f7ff",
                                          border: "1px solid #91d5ff",
                                          borderRadius: "3px",
                                          padding: "2px 4px",
                                          fontSize: "10px",
                                          fontWeight: "500",
                                          color: "#0050b3",
                                          display: "inline-block",
                                          marginTop: "1px",
                                        }}
                                      >
                                        📦 {tariff.bracketDisplay}
                                      </div>
                                    )}
                                  </div>

                                  {tariff.from && tariff.to && (
                                    <Text
                                      type="secondary"
                                      style={{
                                        fontSize: "10px",
                                        display: "block",
                                        marginBottom: "3px",
                                        fontWeight: "500",
                                      }}
                                    >
                                      {new Date(
                                        tariff.from
                                      ).toLocaleDateString()}{" "}
                                      -{" "}
                                      {new Date(tariff.to).toLocaleDateString()}
                                    </Text>
                                  )}

                                  {tariff.matchReasons &&
                                    tariff.matchReasons.length > 0 && (
                                      <div style={{ marginBottom: "4px" }}>
                                        {tariff.matchReasons.map(
                                          (reason, idx) => (
                                            <span
                                              key={idx}
                                              style={{
                                                fontSize: "9px",
                                                background:
                                                  reason === "volume bracket"
                                                    ? "#52c41a"
                                                    : "#e6f7ff",
                                                color:
                                                  reason === "volume bracket"
                                                    ? "white"
                                                    : "#1890ff",
                                                padding: "2px 4px",
                                                borderRadius: "2px",
                                                marginRight: "2px",
                                                fontWeight: "500",
                                              }}
                                            >
                                              {reason === "volume bracket"
                                                ? "📦 CBM"
                                                : reason.replace(" ", "")}
                                            </span>
                                          )
                                        )}
                                      </div>
                                    )}
                                </div>

                                <Button
                                  type={isSelected ? "primary" : "default"}
                                  size="small"
                                  block
                                  style={{
                                    fontSize: "10px",
                                    fontWeight: "500",
                                    height: "20px",
                                    padding: "0 4px",
                                  }}
                                >
                                  {isSelected ? "✓" : "Select"}
                                </Button>
                              </Card>
                            );
                          })}
                        </div>
                      )}
                    </div>
                  </Col>
                </Row>
              )}
            </Card>
          )}

          {/* Customs Calculation Box */}
          <CustomsSection
            selectedBooking={selectedBooking}
            onAddCustomsLine={addCustomsToInvoiceLines}
          />

          {/* LMD Calculation Box */}
          <LMDSection
            selectedBooking={selectedBooking}
            onAddLMDLine={addLMDToInvoiceLines}
          />

          {/* Existing Tariff Lines - Only shown in edit mode */}
          {isEditMode &&
            (() => {
              const existingTariffLines = getTariffLines();
              return existingTariffLines.length > 0 ? (
                <Card
                  size="small"
                  style={{
                    marginBottom: 16,
                    border: "1px solid #d9d9d9",
                  }}
                  title={
                    <span style={{ fontWeight: "bold" }}>
                      📋 Existing Tariff Lines ({existingTariffLines.length})
                    </span>
                  }
                >
                  {existingTariffLines.map(
                    (
                      tariffLine: FormValues["lines"][0] & { index: number }
                    ) => (
                      <Card
                        key={tariffLine.index}
                        size="small"
                        style={{ marginBottom: 8 }}
                        bodyStyle={{ padding: "8px 12px" }}
                      >
                        <Row gutter={12} align="middle">
                          <Col span={12}>
                            <Text strong style={{ fontSize: "13px" }}>
                              {tariffLine.description}
                            </Text>
                            <br />
                            <Text type="secondary" style={{ fontSize: "11px" }}>
                              Qty: {tariffLine.quantity} × €
                              {tariffLine.unit_price}
                              {tariffLine.vat_percentage &&
                                ` (VAT: ${tariffLine.vat_percentage}%)`}
                            </Text>
                          </Col>
                          <Col span={6}>
                            <Text strong style={{ fontSize: "13px" }}>
                              €
                              {(
                                (tariffLine.quantity || 0) *
                                (tariffLine.unit_price || 0)
                              ).toFixed(2)}
                            </Text>
                          </Col>
                          <Col span={6}>
                            <Space>
                              <Button
                                size="small"
                                type="link"
                                onClick={() => editTariffLine(tariffLine.index)}
                                style={{ padding: "0 4px" }}
                              >
                                Edit
                              </Button>
                              <Button
                                size="small"
                                type="link"
                                danger
                                onClick={() =>
                                  removeTariffLine(tariffLine.index)
                                }
                                style={{ padding: "0 4px" }}
                              >
                                Remove
                              </Button>
                            </Space>
                          </Col>
                        </Row>
                      </Card>
                    )
                  )}
                </Card>
              ) : null;
            })()}

          {/* Existing Customs Lines - Only shown in edit mode */}
          {isEditMode &&
            (() => {
              const existingCustomsLines = getCustomsLines();
              return existingCustomsLines.length > 0 ? (
                <Card
                  size="small"
                  style={{
                    marginBottom: 16,
                    border: "1px solid #52c41a",
                  }}
                  title={
                    <span style={{ fontWeight: "bold", color: "#52c41a" }}>
                      🛃 Existing Customs Lines ({existingCustomsLines.length})
                    </span>
                  }
                >
                  {existingCustomsLines.map(
                    (
                      customsLine: FormValues["lines"][0] & { index: number }
                    ) => (
                      <Card
                        key={customsLine.index}
                        size="small"
                        style={{ marginBottom: 8 }}
                        bodyStyle={{ padding: "8px 12px" }}
                      >
                        <Row gutter={12} align="middle">
                          <Col span={12}>
                            <Text strong style={{ fontSize: "13px" }}>
                              {customsLine.description}
                            </Text>
                            <br />
                            <Text type="secondary" style={{ fontSize: "11px" }}>
                              Qty: {customsLine.quantity} × €
                              {customsLine.unit_price}
                              {customsLine.vat_percentage &&
                                ` (VAT: ${customsLine.vat_percentage}%)`}
                            </Text>
                          </Col>
                          <Col span={6}>
                            <Text strong style={{ fontSize: "13px" }}>
                              €
                              {(
                                (customsLine.quantity || 0) *
                                (customsLine.unit_price || 0)
                              ).toFixed(2)}
                            </Text>
                          </Col>
                          <Col span={6}>
                            <Space>
                              <Button
                                size="small"
                                type="link"
                                danger
                                onClick={() =>
                                  removeCustomsLine(customsLine.index)
                                }
                                style={{ padding: "0 4px" }}
                              >
                                Remove
                              </Button>
                            </Space>
                          </Col>
                        </Row>
                      </Card>
                    )
                  )}
                </Card>
              ) : null;
            })()}

          {/* Existing LMD Lines - Only shown in edit mode */}
          {isEditMode &&
            (() => {
              const existingLMDLines = getLMDLines();
              return existingLMDLines.length > 0 ? (
                <Card
                  size="small"
                  style={{
                    marginBottom: 16,
                    border: "1px solid #722ed1",
                  }}
                  title={
                    <span style={{ fontWeight: "bold", color: "#722ed1" }}>
                      🚚 Existing LMD Lines ({existingLMDLines.length})
                    </span>
                  }
                >
                  {existingLMDLines.map(
                    (lmdLine: FormValues["lines"][0] & { index: number }) => (
                      <Card
                        key={lmdLine.index}
                        size="small"
                        style={{ marginBottom: 8 }}
                        bodyStyle={{ padding: "8px 12px" }}
                      >
                        <Row gutter={12} align="middle">
                          <Col span={12}>
                            <Text strong style={{ fontSize: "13px" }}>
                              {lmdLine.description}
                            </Text>
                            <br />
                            <Text type="secondary" style={{ fontSize: "11px" }}>
                              Qty: {lmdLine.quantity} × €{lmdLine.unit_price}
                              {lmdLine.vat_percentage &&
                                ` (VAT: ${lmdLine.vat_percentage}%)`}
                            </Text>
                          </Col>
                          <Col span={6}>
                            <Text strong style={{ fontSize: "13px" }}>
                              €
                              {(
                                (lmdLine.quantity || 0) *
                                (lmdLine.unit_price || 0)
                              ).toFixed(2)}
                            </Text>
                          </Col>
                          <Col span={6}>
                            <Space>
                              <Button
                                size="small"
                                type="link"
                                danger
                                onClick={() => removeLMDLine(lmdLine.index)}
                                style={{ padding: "0 4px" }}
                              >
                                Remove
                              </Button>
                            </Space>
                          </Col>
                        </Row>
                      </Card>
                    )
                  )}
                </Card>
              ) : null;
            })()}

          <Form.List name="lines">
            {(fields, { add, remove }) => (
              <>
                {/* Compact Table Format for Invoice Lines */}
                <Table
                  dataSource={fields.map((field, index) => ({
                    fieldKey: field.key,
                    index,
                    ...field,
                  }))}
                  pagination={false}
                  size="small"
                  style={{ marginBottom: 12 }}
                  scroll={{ x: true }}
                  columns={[
                    {
                      title: "Description",
                      dataIndex: "description",
                      key: "description",
                      width: "45%",
                      render: (_, record) => {
                        const lines = formValues.lines || [];
                        const line = lines[record.index];

                        // Show cost type description for non-tariff items
                        let costTypeDisplay = "";
                        if (line?.cost_type) {
                          if (line.cost_type === "tariff") {
                            costTypeDisplay = "Tariff";
                          } else if (line.cost_type === "customs_btw") {
                            costTypeDisplay = "BTW bij Invoer";
                          } else if (line.cost_type === "customs_duties") {
                            costTypeDisplay = "Invoerrechten";
                          } else if (line.cost_type === "customs_commission") {
                            costTypeDisplay = "Voorschotprovisie";
                          } else if (line.cost_type === "lmd_delivery") {
                            costTypeDisplay = "Last Mile Delivery";
                          } else {
                            // Find the component to get its description
                            // Try to match by ID first, then by name (for backward compatibility)
                            const component = pricingComponents.find(
                              (comp) =>
                                comp.id.toString() ===
                                  line.cost_type?.toString() ||
                                comp.name === line.cost_type
                            );
                            costTypeDisplay =
                              component?.description ||
                              component?.name ||
                              line.cost_type;
                          }
                        }

                        return (
                          <div>
                            <Form.Item
                              name={[record.name, "description"]}
                              rules={[{ required: true, message: "Required" }]}
                              style={{ marginBottom: 4 }}
                            >
                              <Input
                                placeholder="e.g. Freight charges"
                                size="small"
                                onBlur={() => {
                                  if (isEditMode) {
                                    const lines = form.getFieldValue("lines");
                                    if (lines && lines[record.index]) {
                                      autoSaveInvoiceLine(
                                        record.index,
                                        lines[record.index]
                                      );
                                    }
                                  }
                                }}
                              />
                            </Form.Item>

                            {/* Cost Type Dropdown */}
                            <Form.Item
                              name={[record.name, "cost_type"]}
                              style={{ marginBottom: 0 }}
                            >
                              <Select
                                placeholder="Select cost type"
                                size="small"
                                style={{ fontSize: "10px" }}
                                optionLabelProp="label"
                                value={(() => {
                                  const lines = formValues.lines || [];
                                  const line = lines[record.index];
                                  return line?.cost_type;
                                })()}
                                onChange={(value) => {
                                  // Update description based on selected component
                                  const selectedComponent =
                                    pricingComponents.find(
                                      (component) =>
                                        component.id.toString() === value
                                    );
                                  if (selectedComponent) {
                                    const description =
                                      selectedComponent.description ||
                                      selectedComponent.name;
                                    const lines = form.getFieldValue("lines");
                                    if (lines && lines[record.index]) {
                                      lines[record.index].description =
                                        description;
                                      form.setFieldsValue({ lines });
                                    }
                                  }

                                  if (isEditMode) {
                                    setTimeout(() => {
                                      const lines = form.getFieldValue("lines");
                                      if (lines && lines[record.index]) {
                                        autoSaveInvoiceLine(
                                          record.index,
                                          lines[record.index]
                                        );
                                      }
                                    }, 100);
                                  }
                                }}
                              >
                                {pricingComponents.map(
                                  (component: PricingComponent) => (
                                    <Option
                                      key={component.id}
                                      value={component.id.toString()}
                                      label={
                                        component.description || component.name
                                      }
                                    >
                                      <div>
                                        <Text strong>{component.name}</Text>
                                        <br />
                                        <Text
                                          type="secondary"
                                          style={{ fontSize: "10px" }}
                                        >
                                          {component.description}
                                        </Text>
                                      </div>
                                    </Option>
                                  )
                                )}
                              </Select>
                            </Form.Item>

                            {/* Show current cost type if selected */}
                            {costTypeDisplay && (
                              <Text
                                type="secondary"
                                style={{
                                  fontSize: "10px",
                                  fontStyle: "italic",
                                }}
                              >
                                {costTypeDisplay}
                              </Text>
                            )}
                          </div>
                        );
                      },
                    },
                    {
                      title: "Qty",
                      dataIndex: "quantity",
                      key: "quantity",
                      width: "12%",
                      render: (_, record) => (
                        <Form.Item
                          name={[record.name, "quantity"]}
                          rules={[{ required: true, message: "Required" }]}
                          style={{ marginBottom: 0 }}
                        >
                          <InputNumber
                            min={0}
                            size="small"
                            style={{ width: "100%" }}
                            onBlur={() => {
                              if (isEditMode) {
                                const lines = form.getFieldValue("lines");
                                if (lines && lines[record.index]) {
                                  autoSaveInvoiceLine(
                                    record.index,
                                    lines[record.index]
                                  );
                                }
                              }
                            }}
                          />
                        </Form.Item>
                      ),
                    },
                    {
                      title: "Unit Price",
                      dataIndex: "unit_price",
                      key: "unit_price",
                      width: "15%",
                      render: (_, record) => (
                        <Form.Item
                          name={[record.name, "unit_price"]}
                          rules={[{ required: true, message: "Required" }]}
                          style={{ marginBottom: 0 }}
                        >
                          <InputNumber
                            min={0}
                            step={0.01}
                            precision={2}
                            size="small"
                            style={{ width: "100%" }}
                            formatter={(value) =>
                              value
                                ? `€${Number(value).toFixed(2)}`.replace(
                                    /\B(?=(\d{3})+(?!\d))/g,
                                    ","
                                  )
                                : "€0.00"
                            }
                            parser={(value) => {
                              const parsed = value
                                ? value.replace(/€\s?|(,*)/g, "")
                                : "0";
                              return parseFloat(parsed) as unknown as 0;
                            }}
                            onFocus={(e) => {
                              const lines = formValues.lines || [];
                              const line = lines[record.index];
                              if (line && line.unit_price === 0) {
                                e.target.select();
                              }
                            }}
                            onBlur={() => {
                              if (isEditMode) {
                                const lines = form.getFieldValue("lines");
                                if (lines && lines[record.index]) {
                                  autoSaveInvoiceLine(
                                    record.index,
                                    lines[record.index]
                                  );
                                }
                              }
                            }}
                          />
                        </Form.Item>
                      ),
                    },
                    {
                      title: "VAT %",
                      dataIndex: "vat_percentage",
                      key: "vat_percentage",
                      width: "10%",
                      render: (_, record) => (
                        <Form.Item
                          name={[record.name, "vat_percentage"]}
                          rules={[{ required: true, message: "Required" }]}
                          style={{ marginBottom: 0 }}
                        >
                          <InputNumber
                            min={0}
                            max={100}
                            size="small"
                            style={{ width: "100%" }}
                            placeholder="0"
                            defaultValue={0}
                            onBlur={() => {
                              if (isEditMode) {
                                const lines = form.getFieldValue("lines");
                                if (lines && lines[record.index]) {
                                  autoSaveInvoiceLine(
                                    record.index,
                                    lines[record.index]
                                  );
                                }
                              }
                            }}
                          />
                        </Form.Item>
                      ),
                    },
                    {
                      title: "Total",
                      dataIndex: "total",
                      key: "total",
                      width: "12%",
                      render: (_, record) => {
                        const lines = formValues.lines || [];
                        const line = lines[record.index];
                        const total = line
                          ? (line.quantity || 0) * (line.unit_price || 0)
                          : 0;
                        return (
                          <div
                            style={{ display: "flex", alignItems: "center" }}
                          >
                            <Text strong style={{ fontSize: "12px" }}>
                              €{total.toFixed(2)}
                            </Text>
                            {savingLineIndex === record.index && (
                              <LoadingOutlined
                                style={{ marginLeft: 4, fontSize: "10px" }}
                              />
                            )}
                          </div>
                        );
                      },
                    },
                    {
                      title: "Action",
                      key: "action",
                      width: "6%",
                      render: (_, record) =>
                        fields.length > 1 && (
                          <Button
                            danger
                            size="small"
                            icon={<DeleteOutlined />}
                            onClick={() => {
                              remove(record.name);
                              // Auto-save after removing a line
                              if (isEditMode) {
                                setTimeout(() => {
                                  const updatedFormValues = {
                                    ...formValues,
                                    lines: form.getFieldValue("lines") || [],
                                  };
                                  setFormValues(updatedFormValues);
                                  autoSaveInvoice(updatedFormValues);
                                }, 100);
                              }
                            }}
                          />
                        ),
                    },
                  ]}
                />

                <Form.Item style={{ marginBottom: 8 }}>
                  <Space direction="vertical" style={{ width: "100%" }}>
                    <Button
                      type="dashed"
                      onClick={() => {
                        add({
                          description: "",
                          quantity: 1,
                          unit_price: 0,
                          vat_percentage: 0,
                          cost_type: "",
                        });
                      }}
                      icon={<PlusOutlined />}
                      block
                      size="small"
                    >
                      Add Line Item
                    </Button>

                    {selectedBooking && (
                      <div>
                        <Button
                          type="default"
                          onClick={() =>
                            autogenerateInvoiceLines(selectedBooking)
                          }
                          loading={autogeneratingLines}
                          block
                          size="small"
                          icon={<InfoCircleOutlined />}
                        >
                          <Tooltip
                            title={
                              isEditMode
                                ? "WARNING: This will overwrite your existing saved invoice lines with new lines generated from pricing rules based on transport type, origin/destination, and calculate quantities from weight, volume, or product count."
                                : "This will match the booking against pricing rules based on transport type, origin/destination, and calculate quantities from weight, volume, or product count."
                            }
                          >
                            Regenerate Lines from Pricing Rules
                          </Tooltip>
                        </Button>

                        {selectedBooking.container_info && (
                          <div
                            style={{
                              marginTop: 4,
                              fontSize: "11px",
                              color: "#666",
                              textAlign: "center",
                            }}
                          >
                            {(() => {
                              const containerInfo = Array.isArray(
                                selectedBooking.container_info
                              )
                                ? selectedBooking.container_info[0]
                                : selectedBooking.container_info;

                              let infoMessage = "";
                              let tooltipMessage = "";

                              // Check for container ETD date
                              if (containerInfo && containerInfo.etd) {
                                const etdDate = new Date(containerInfo.etd);
                                infoMessage = `Using container ETD tariff: ${etdDate.toLocaleDateString()}`;
                                tooltipMessage =
                                  "Pricing rules will be matched based on this container's ETD date";
                              } else {
                                infoMessage = "Using current date for pricing";
                                tooltipMessage =
                                  "Using current date for pricing rules as no container ETD is available";
                              }

                              // Check for container ports
                              let usingContainerPorts = false;
                              if (containerInfo) {
                                if (
                                  containerInfo.origin_port ||
                                  containerInfo.destination_port
                                ) {
                                  usingContainerPorts = true;
                                  tooltipMessage +=
                                    "\nUsing container's origin and/or destination ports for matching";
                                }
                              }

                              return (
                                <Tooltip title={tooltipMessage}>
                                  <InfoCircleOutlined
                                    style={{ marginRight: 4 }}
                                  />
                                  {infoMessage}
                                  {usingContainerPorts && (
                                    <Tag
                                      color="blue"
                                      style={{
                                        marginLeft: 4,
                                        fontSize: "10px",
                                      }}
                                    >
                                      Container ports
                                    </Tag>
                                  )}
                                </Tooltip>
                              );
                            })()}
                          </div>
                        )}
                      </div>
                    )}
                  </Space>
                </Form.Item>
              </>
            )}
          </Form.List>

          <Divider style={{ margin: "8px 0" }} />

          <Row justify="end">
            <Col>
              <Space>
                <Text strong>
                  Total Amount: €{" "}
                  {calculateTotal(formValues.lines || []).toFixed(2)}
                </Text>
              </Space>
            </Col>
          </Row>

          <Form.Item style={{ marginTop: 16, marginBottom: 0 }}>
            <Space direction="vertical" style={{ width: "100%" }}>
              <Space>
                <Button
                  type="primary"
                  htmlType="submit"
                  loading={loading}
                  size="small"
                >
                  {isEditMode ? "Update Invoice" : "Create Invoice"}
                </Button>
                <Button onClick={onCancel} size="small">
                  Cancel
                </Button>
              </Space>
              {isEditMode && (
                <Text type="secondary" style={{ fontSize: "11px" }}>
                  💾 Changes are automatically saved as you edit
                </Text>
              )}
            </Space>
          </Form.Item>
        </Form>
      </Card>

      {/* Invoice Preview Modal */}
      <Modal
        title="Invoice Preview"
        open={previewModalVisible}
        onCancel={() => setPreviewModalVisible(false)}
        footer={[
          <Button key="close" onClick={() => setPreviewModalVisible(false)}>
            Close
          </Button>,
        ]}
        width={800}
        style={{ top: 20 }}
      >
        <InvoicePreview invoice={formValues} booking={selectedBooking} />
      </Modal>

      {/* Pricing Information Modal */}
      <Modal
        title="How Pricing Components Work"
        open={pricingInfoVisible}
        onCancel={() => setPricingInfoVisible(false)}
        footer={[
          <Button
            key="close"
            size="small"
            onClick={() => setPricingInfoVisible(false)}
          >
            Close
          </Button>,
        ]}
        width={600}
      >
        <div style={{ maxHeight: "400px", overflow: "auto" }}>
          <h4>Pricing Calculation Process</h4>
          <p>
            When you select a booking, the system automatically generates
            invoice lines by:
          </p>
          <ol>
            <li>
              <strong>Matching pricing rules</strong> to the booking based on:
              <ul>
                <li>Transport type (Air, Sea, Road)</li>
                <li>Origin and destination ports</li>
                <li>Shipping method (FCL, LCL, etc.)</li>
                <li>Container size (if applicable)</li>
                <li>
                  <strong>Date validity</strong>: The system uses the
                  container's ETD date (if available) to match pricing rules
                  that were valid at that time. If no container ETD is
                  available, the current date is used.
                </li>
              </ul>
            </li>
            <li>
              <strong>Calculating quantities</strong> based on the pricing
              component type:
              <ul>
                <li>
                  <strong>per_kg</strong>: Uses the total weight from all
                  products
                </li>
                <li>
                  <strong>per_cbm</strong>: Uses the total volume from all
                  products
                </li>
                <li>
                  <strong>per_product</strong>: Uses the total count of unique
                  products from the booking. Supports included_products and
                  extra_product_cost for tiered pricing.
                </li>
                <li>
                  <strong>per_unique_product</strong>: Uses the count of unique
                  products based on HS codes from the booking. Similar to
                  customs clearance calculation. Supports included_products and
                  extra_product_cost for tiered pricing.
                </li>
                <li>
                  <strong>per_carton</strong>: Uses the total number of cartons
                  from all products
                </li>
                <li>
                  <strong>fixed</strong>: Uses a quantity of 1 for a flat rate
                </li>
              </ul>
            </li>
            <li>
              <strong>Grouping components</strong> by their type to organize the
              invoice:
              <ul>
                <li>Freight charges</li>
                <li>Handling fees</li>
                <li>Documentation fees</li>
                <li>Customs clearance</li>
                <li>Other services</li>
              </ul>
            </li>
          </ol>
          <h4>Manual Adjustments</h4>
          <p>
            You can manually adjust any generated line item or add new ones as
            needed. If pricing rules change, or you need to recalculate based on
            the booking, use the "Regenerate Lines from Pricing Rules" button.
          </p>
        </div>
      </Modal>
    </>
  );
};

export default InvoiceForm;
